import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Search, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { LeafletMap } from '@/components/transport/LeafletMap';
import { transportApi } from '@/services/transportApi';

const TYPES = [['site', 'Company Site'], ['warehouse', 'Warehouse'], ['customer', 'Customer'], ['vendor', 'Vendor'], ['depot', 'Depot'], ['other', 'Other']];
const EMPTY = { name: '', type: 'site', lat: null, lng: null, address: '', active: true };

const Locations = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = async () => { try { setRows(await transportApi.listLocations({ active: true })); } catch { toast.error('Failed to load locations'); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setResults([]); setQ(''); setOpen(true); };
  const openEdit = (l) => { setForm({ ...EMPTY, ...l }); setEditId(l.id); setResults([]); setQ(''); setOpen(true); };

  const doSearch = async () => {
    if (!q.trim() || q.trim().length < 2) { toast.error('Type an address to search'); return; }
    setSearching(true);
    try {
      const res = await transportApi.geocode(q.trim());
      setResults(res || []);
      if (!res || res.length === 0) toast.message('No matches — try a broader search or drop a pin on the map');
    } catch (e) { toast.error(e.response?.data?.detail || 'Address search unavailable'); } finally { setSearching(false); }
  };

  const pickResult = (r) => {
    setForm(f => ({ ...f, lat: r.lat, lng: r.lng, address: r.display_name || f.address, name: f.name || r.name }));
    setResults([]);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.lat == null || form.lng == null) { toast.error('Set the location on the map (search or tap the map)'); return; }
    try {
      const payload = { ...form, lat: Number(form.lat), lng: Number(form.lng) };
      if (editId) await transportApi.updateLocation(editId, payload);
      else await transportApi.createLocation(payload);
      toast.success('Location saved'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => { if (!window.confirm('Remove this saved location?')) return; await transportApi.deleteLocation(id); load(); };

  const markers = (form.lat != null && form.lng != null)
    ? [{ lat: form.lat, lng: form.lng, color: '#F59E0B', label: form.name || 'Here', draggable: true, onDragEnd: (la, ln) => setForm(f => ({ ...f, lat: la, lng: ln })) }]
    : [];

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-locations-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
            <div><h1 className="text-xl font-bold font-display">Saved Locations</h1><p className="text-xs text-[#E8DCC8]">The places trips run between — pinned once, reused everywhere</p></div>
          </div>
          <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600" data-testid="location-add-btn"><Plus className="w-4 h-4 mr-1" />Add Location</Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Card className="border-0 shadow-md"><CardContent className="p-0">
          <Table data-testid="locations-table">
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Address</TableHead><TableHead>Coordinates</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map(l => (
                <TableRow key={l.id} data-testid={`location-row-${l.id}`}>
                  <TableCell className="font-semibold flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-amber-500" />{l.name}</TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize">{l.type}</Badge></TableCell>
                  <TableCell className="text-xs text-gray-500 max-w-[280px] truncate">{l.address || '—'}</TableCell>
                  <TableCell className="text-xs text-gray-500">{Number(l.lat).toFixed(4)}, {Number(l.lng).toFixed(4)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid={`location-edit-${l.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(l.id)} data-testid={`location-delete-${l.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-10" data-testid="locations-empty">No saved locations yet. Add Head Office, your warehouses and regular stops.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="location-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Head Office" data-testid="location-name-input" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="location-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Find on map (search address)</Label>
              <div className="flex gap-2">
                <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()} placeholder="Search a place / address" data-testid="location-search-input" />
                <Button variant="outline" onClick={doSearch} disabled={searching} data-testid="location-search-btn">{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
              </div>
              {results.length > 0 && (
                <div className="mt-2 border rounded-lg divide-y max-h-40 overflow-y-auto" data-testid="location-search-results">
                  {results.map((r, i) => (
                    <button key={i} onClick={() => pickResult(r)} className="w-full text-left px-3 py-2 text-xs hover:bg-amber-50" data-testid={`location-result-${i}`}>
                      <span className="font-medium">{r.name}</span><span className="text-gray-400"> — {r.display_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <LeafletMap height="260px" markers={markers}
              center={form.lat != null ? [form.lat, form.lng] : [11.6643, 78.1460]}
              onMapClick={(la, ln) => setForm(f => ({ ...f, lat: la, lng: ln }))} testId="location-map" />
            <p className="text-xs text-gray-500">Tap the map or drag the pin to fine-tune. {form.lat != null ? `Pinned: ${Number(form.lat).toFixed(4)}, ${Number(form.lng).toFixed(4)}` : 'No pin set yet.'}</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-amber-500 hover:bg-amber-600" data-testid="location-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Locations;
